import clsx from 'clsx'
import {useFormik} from 'formik'
import React, {useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import * as Yup from 'yup'
import useCancelToken from '../../../../../../_gori/hooks/UseCancelToken'
import {
  Button,
  InputTextFormik,
  ValidationErrorModal,
} from '../../../../../../_gori/partials/widgets'
import {InputCheckboxFormik} from '../../../../../../_gori/partials/widgets/form/InputCheckboxFormik'
import {ConfigGeneral} from '../../../core/_const'
import SettingsService from '../../../core/_requests'
import {StorageHelpers} from '../../../../../../_gori/helpers'

type Props = {
  show: boolean
  handleClose: () => void
  data?: any
  handleReload: any
}

const CustomerTrackingNotificationModal: React.FC<Props> = ({
  show,
  handleClose,
  data,
  handleReload,
}) => {
  const intl = useIntl()
  const {newCancelToken} = useCancelToken()
  const [validationErrors, setValidationErrors] = useState<any>()

  const validationSchema = Yup.object().shape({
    status: Yup.boolean(),
    name: Yup.string().when('status', {
      is: (val) => val === true,
      then: Yup.string()
        .required(intl.formatMessage({id: 'REQUIRED'}))
        .max(
          100,
          intl.formatMessage(
            {id: 'INPUT_MUST_NOT_BE_GREATER_THAN_CHARACTERS'},
            {input: intl.formatMessage({id: 'COMPANY'}), max: 100}
          )
        ),
    }),
  })

  const formik = useFormik({
    initialValues: {
      status: data?.status ?? false,
      name: data?.value?.name ?? '',
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, {setSubmitting}) => {
      try {
        setSubmitting(true)
        const config = {cancelToken: newCancelToken()}
        const payload = {
          category: data?.category ?? ConfigGeneral.NOTIFICATION_CATEGORY.TRACKING,
          type: data?.type ?? ConfigGeneral.NOTIFICATION_TYPE.EMAIL,
          status: values.status,
          value: {name: values.name || data?.value?.name},
        }
        const res = data?.id
          ? await SettingsService.updateNotification(data?.id, payload, config)
          : await SettingsService.createNotification(payload, config)
        if (res) {
          toast.success(intl.formatMessage({id: res.message}))
          handleReload()
          handleCloseModal()
        }
      } catch (error: any) {
        setValidationErrors(error?.response)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleCloseModal = () => {
    handleClose()
    formik.resetForm()
  }

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}

      <Modal
        id='gori_modal_change_password'
        tabIndex={-1}
        aria-hidden='true'
        centered
        dialogClassName='modal-size-md h-auto'
        contentClassName='modal-padding'
        show={show}
        backdrop='static'
        onHide={handleCloseModal}
      >
        <Modal.Header closeButton>
          <Modal.Title bsPrefix={'fw-bolder fs-1'}>
            {intl.formatMessage({id: 'CUSTOMER_TRACKING_NOTIFICATIONS'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='vh-75 overflow-auto'>
          <div className='d-flex justify-content-between'>
            <p className='form-label'>{intl.formatMessage({id: 'ALERT'})}</p>
            <InputCheckboxFormik
              formik={formik}
              className='form-switch ms-9 mt-1'
              type='checkbox'
              name='status'
              disabled={formik.isSubmitting}
            />
          </div>
          {formik.values.status && (
            <>
              <div className={clsx('my-8')}></div>
              <InputTextFormik
                className='w-100'
                required
                label={intl.formatMessage({id: 'COMPANY'})}
                formik={formik}
                name='name'
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className='d-flex justify-content-end'>
            <Button
              className='btn btn-advanced-secondary me-2'
              label={intl.formatMessage({id: 'CANCEL'})}
              loadingText={intl.formatMessage({id: 'CANCEL'})}
              event={handleCloseModal}
              disabled={formik.isSubmitting}
            />
            <Button
              className='btn btn-advanced-primary'
              label={intl.formatMessage({id: 'SAVE'})}
              loadingText={intl.formatMessage({id: 'SAVE'})}
              disabled={formik.isSubmitting}
              event={() => {
                StorageHelpers.setItemLocalStorage('cardnotification', {hidden: true})
                window.dispatchEvent(new Event('storage'))
                formik.submitForm()
              }}
              loading={formik.isSubmitting}
            />
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export {CustomerTrackingNotificationModal}
